import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/styles';
import useSnackbar from 'utils/useSnackbar'
import useRouter from 'utils/useRouter';
import tree from 'utils/tree';
import Classes from 'services/classes';
import { Page } from 'components';
import { Folders, GroupPage } from './components';

const useStyles = makeStyles(theme => ({
  root: {
    height: '100%',
    display: 'flex',
    overflow: 'hidden',
    transition: theme.transitions.create('transform', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    }),
    '@media (max-width: 780px)': {
      '& $folders, & $groupDetails': {
        flexBasis: '100%',
        width: '100%',
        maxWidth: 'none',
        flexShrink: '0',
        transition: 'transform .5s ease',
        transform: 'translateX(0)'
      }
    }
  },
  openFolder: {
    '@media (max-width: 780px)': {
      '& $folders, & $groupDetails': {
        transform: 'translateX(-100%)'
      }
    }
  },
  folders: {
    flexBasis: 280,
    flexShrink: 0,
    flexGrow: 0,
    borderRight: `1px solid ${theme.palette.divider}`
  },
  groupDetails: {
    flexGrow: 1
  }
}));

const Group = () => {
  const classes = useStyles();
  const router = useRouter();
  const [folders, setFolders] = useState([]);
  const [customers] = useState([]);
  const [group, setGroup] = useState({ open: false, values: {} });

  const handleAlert = useSnackbar();

  useEffect(() => {
    const { token: cancelToken, cancel } = Classes.source();

    getFolders({ cancelToken });

    return () => {
      cancel('Abort');
    };
  }, []);

  useEffect(() => {
    if (!router.match.params.id) {
      setGroup({ open: false, values: {} })
    }
  }, [router.match.params.id]);

  const handleFolderOpen = (group) => {
    getStudentsRepositories(group)
  };

  const handleFolderClose = () => {
    setGroup(state => ({ ...state, open: false }));
  };

  async function getFolders({ cancelToken }) {
    try {
      const { data } = await Classes.getWithFolders({}, cancelToken);
      const find = data.classes.find(item => `/groups/${item.classID}` === router.location.pathname);
      if (find) getStudentsRepositories(find);
      setFolders(tree(data.folders, data.classes));
    } catch (err) {
      handleAlert(err);
    }
  }

  async function getStudentsRepositories(group) {
    try {
      const { data } = await Classes.getStudentsRepositories({ classID: group.classID });
      setGroup({ values: { ...group, ...data }, open: true });
    } catch (err) {
      handleAlert(err);
    }
  }

  const handleCreateFolder = (name, close) => {
    Classes.createFolder({ name, parent: 0 })
      .then(getFolders)
      .then(close)
      .catch(handleAlert);
  }

  const handleDeleteFolder = (folderID, close) => {
    Classes.deleteFolder({ folderID })
      .then(getFolders)
      .then(close)
      .catch(handleAlert);
  }

  const handleDuplicateGroup = (classID) => (name, close) => {
    Classes.duplicateClass({ classID, name })
      .then(getFolders)
      .then(close)
      .catch(handleAlert);
  }

  const handleDeleteGroup = (id, close) => {
    Classes.deleteClass({ classIDs: [id] })
      .then(getFolders)
      .then(close)
      .then(() => {
        router.history.replace('/groups')
        setGroup({ values: {}, open: false });
      })
      .catch(handleAlert);
  }

  const handleCreateGroup = ({ folderID, ...rest }, close) => {
    Classes.createClass([{ ...rest, folderID: folderID === true ? 0 : folderID }])
      .then(getFolders)
      .then(close)
      .catch(handleAlert);
  }

  const handleEditGroup = (values, close) => {
    Classes.editClass(values)
      .then(getFolders)
      .then(close)
      .catch(handleAlert);
  }

  const handleDrop = (folder, id) => {
    Classes.changeFolder({ classes: [id], folders: [], folder })
      .then(getFolders)
      .catch(handleAlert);
  }

  const handleAddMember = (classID) => async ({ email, emails }, close) => {
    try {
      if (email) {
        await Classes.addStudent({ classID, studentLoginID: email });
        close();
      } else if (emails) {
        await Classes.addStudents({ classID, students: emails });
      }
    } catch (err) {
      handleAlert(err);
    } finally {
      handleAlert({
        message: `A group invitation has been sent to ${emails && emails.length > 1 ? 'the students':'the student'}`,
        severity: 'success'
      })
      close();
    }
  }

  const handleRemoveMembers = async (classID, studentIDs, close) => {
    try {
      await Classes.removeStudent({ classIDs: [classID], studentIDs })
      const { data } = await Classes.getStudents({ classID });
      setGroup(group => ({ ...group, values: { ...group.values, students: data } }));
      close();
    } catch (err) {
      handleAlert(err)
    }
  }

  const handleShareRepository = (classID) => async (shared, repoID) => {
    try {
      if (shared) {
        await Classes.removeRepository({ classIDs: [classID], repoID: [repoID] })
      } else {
        await Classes.addRepository({ classIDs: [classID], repoID: [repoID] })
      }
      const { data } = await Classes.getClassRepositories({ classIDs: [classID] });
      setGroup(group => ({ ...group, values: { ...group.values, repositories: data } }));
    } catch (err) {
      handleAlert(err)
    }
  }

  const handleSendMailstudents = (classID) => ({ students, subject, htmlBody }, close) => {
    close();
    Classes.sendMailstudents({ classID, students, subject, htmlBody })
      .catch(handleAlert)
  }

  return (
    <Page
      className={clsx({
        [classes.root]: true,
        [classes.openFolder]: group.open
      })}
      title="Groups"
    >
      <Folders
        className={classes.folders}
        createFolder={handleCreateFolder}
        createGroup={handleCreateGroup}
        deleteFolder={handleDeleteFolder}
        duplicateGroup={handleDuplicateGroup}
        folders={folders}
        onDropFolder={handleDrop}
        onFolderOpen={handleFolderOpen}
      />
      <GroupPage
        className={classes.groupDetails}
        customers={customers}
        group={group}
        onAddMember={handleAddMember}
        onBack={handleFolderClose}
        onDeleteGroup={handleDeleteGroup}
        onEditGroup={handleEditGroup}
        onRefresh={getFolders}
        onRemoveMembers={handleRemoveMembers}
        onSendMailStudents={handleSendMailstudents}
        onShareRepository={handleShareRepository}
      />
    </Page>
  );
};

export default Group;
